import { dealFormField, pmpDealType } from '@openx/types/deal.ts';
import { useIsMounted } from '@openx/utils/hooks/useIsMounted.ts';
import { TTD_DEMAND_PARTNER_ID } from '@openx/utils/services/config.ts';
import { type MutableRefObject, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router';

import type { DealFormParams } from '../../../../types.ts';
import { DealParticipantsField } from '../fields.ts';
import { isDV360DemandPartner } from '../utils/Participant-utils.ts';

export const useSetFormFields = (
  isEdit: boolean,
  showParticipantBuyersMultiple: boolean,
  refs: Record<string, MutableRefObject<string[]>>,
  fieldPrefix: string
) => {
  const { multipleBuyersIdsRef, singleBuyerIdsRef, mainBuyerIdsRef, multipleBuyersValuesRef } = refs;

  const isMounted = useIsMounted();
  const { setValue, trigger, control } = useFormContext();
  const location = useLocation();
  const observableDemandPartner = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.DSP}` });
  const observableIsDV360IP = useWatch({ control, name: dealFormField.ENABLE_DV360_INVENTORY_PACKAGE });
  const observableDealType = useWatch({ control, name: dealFormField.PMP_DEAL_TYPE });
  const observableBuyerIds = useWatch({ control, name: `${fieldPrefix}${DealParticipantsField.BUYER_IDS}` });
  const dv360DemandPartner = isDV360DemandPartner(observableDemandPartner);
  const useSharedTargeting: DealFormParams['useSharedTargeting'] = location.state?.useSharedTargeting;
  const useAsTemplate: DealFormParams['useAsTemplate'] = location.state?.useAsTemplate;
  const isTTDPartnerAndGuaranteedDealType =
    observableDemandPartner === TTD_DEMAND_PARTNER_ID && observableDealType === pmpDealType.Guaranteed;
  const isEnabledDV360IP = !!observableIsDV360IP;
  const isCreateMode = !isEdit && !useSharedTargeting && !useAsTemplate;
  const shouldClearBuyerIds =
    (isEdit || useSharedTargeting || useAsTemplate) &&
    !showParticipantBuyersMultiple &&
    (isTTDPartnerAndGuaranteedDealType || dv360DemandPartner);

  const shouldSetDV360FormFields = dv360DemandPartner && !observableBuyerIds?.length;
  const shouldSetTTDAndGuaranteedFormFields = isCreateMode && isTTDPartnerAndGuaranteedDealType;

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    if (shouldClearBuyerIds) {
      return setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, [], {
        shouldTouch: true,
      });
    }
    if (shouldSetDV360FormFields) {
      if (isEnabledDV360IP) {
        trigger(`${fieldPrefix}${DealParticipantsField.BUYER_ID}`);
      }
    }
    if (shouldSetTTDAndGuaranteedFormFields) {
      if (isMounted()) {
        setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, [], {
          shouldTouch: true,
        });
      } else {
        setValue(`${fieldPrefix}${DealParticipantsField.BUYER_IDS}`, multipleBuyersValuesRef.current, {
          shouldTouch: true,
        });
      }
    }
  }, [
    dv360DemandPartner,
    isCreateMode,
    isEnabledDV360IP,
    isMounted,
    mainBuyerIdsRef,
    multipleBuyersIdsRef,
    multipleBuyersValuesRef,
    setValue,
    shouldClearBuyerIds,
    shouldSetTTDAndGuaranteedFormFields,
    singleBuyerIdsRef,
    shouldSetDV360FormFields,
    trigger,
    fieldPrefix,
  ]);
};
